import { useState } from "react";
import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Headphones, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import heroContactImg from "@/assets/hero-contact.jpg";

const ContactPage = () => {
  usePageSeo("contact");
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const { getSetting } = useSiteSettings();

  const phone = getSetting("phone", "+1 (809) 123-4567");
  const email = getSetting("email", "info@alwaysontimetransfers.com");
  const address = getSetting("address", "Dominican Republic");
  const whatsappNumber = getSetting("whatsapp_number", "18091234567");

  const contactInfo = [
    { icon: Phone, label: "Phone", value: phone, href: `tel:${phone.replace(/\s/g, "")}` },
    { icon: Mail, label: "Email", value: email, href: `mailto:${email}` },
    { icon: MapPin, label: "Location", value: address, href: null },
    { icon: Clock, label: "Hours", value: "24/7 — Always available", href: null },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      toast({ title: "Message sent!", description: "We'll get back to you shortly." });
      setFormData({ name: "", email: "", message: "" });
      setSending(false);
    }, 1000);
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative h-[70vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: `url(${heroContactImg})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--hero-bg))]/90 via-[hsl(var(--hero-bg))]/70 to-[hsl(var(--hero-bg))]/90" />
        <div className="absolute inset-0 bg-pattern-diagonal opacity-10" />
        
        <Navbar />
        
        <div className="container mx-auto px-4 relative z-10 h-full flex items-center pt-16">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2.5 mb-6">
                <Headphones className="w-4 h-4 text-secondary" />
                <span className="text-hero-foreground text-sm font-medium">24/7 Customer Support</span>
              </div>
              
              <h1 className="font-display text-5xl md:text-7xl font-extrabold text-hero-foreground mb-6 leading-tight">
                Get In <span className="text-gradient-tropical">Touch</span>
              </h1>
              <p className="text-hero-foreground/80 font-sans text-xl leading-relaxed max-w-3xl mx-auto mb-8">
                Need help with your tour or transfer? We're here to provide instant support whenever you need it.
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {[
                  { icon: Zap, label: "Instant Response", desc: "Within 5 minutes" },
                  { icon: MessageCircle, label: "Live Chat", desc: "WhatsApp & Web" },
                  { icon: Phone, label: "Direct Line", desc: "Call anytime" }
                ].map((contact, i) => (
                  <motion.div key={contact.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.1 }}
                    className="backdrop-blur-xl bg-white/10 rounded-2xl p-4 border border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-tropical flex items-center justify-center">
                        <contact.icon className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <div className="text-left">
                        <div className="font-display font-bold text-hero-foreground text-sm">{contact.label}</div>
                        <div className="text-hero-foreground/60 text-xs font-sans">{contact.desc}</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <motion.a initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8 }}
                href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[hsl(142,70%,40%)] to-[hsl(142,70%,35%)] text-white font-sans font-bold text-lg px-8 py-4 rounded-full hover:shadow-2xl transition-all duration-300 hover:scale-105">
                <MessageCircle className="w-6 h-6" />
                Start WhatsApp Chat
              </motion.a>
            </motion.div>
          </div>
        </div>

        {/* Geometric divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-auto">
            <polygon points="0,80 1440,80 1440,40 720,0 0,40" className="fill-background" />
          </svg>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">
                Reach <span className="text-gradient-tropical">Out</span>
              </h2>
              <div className="space-y-4">
                {contactInfo.map((item, i) => (
                  <motion.div key={item.label} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4 p-5 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-tropical transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-gradient-tropical flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="font-sans font-bold text-foreground text-sm">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-muted-foreground font-sans text-sm hover:text-primary transition-colors">{item.value}</a>
                      ) : (
                        <p className="text-muted-foreground font-sans text-sm">{item.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">
                Send a <span className="text-gradient-tropical">Message</span>
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5 p-8 rounded-2xl bg-card border border-border shadow-lg">
                <div>
                  <label className="font-sans text-sm font-semibold text-foreground mb-2 block">Your Name</label>
                  <Input placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="font-sans h-12" />
                </div>
                <div>
                  <label className="font-sans text-sm font-semibold text-foreground mb-2 block">Email Address</label>
                  <Input type="email" placeholder="john@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="font-sans h-12" />
                </div>
                <div>
                  <label className="font-sans text-sm font-semibold text-foreground mb-2 block">Your Message</label>
                  <Textarea placeholder="Tell us about your travel plans..." value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={5} className="font-sans" />
                </div>
                <Button type="submit" disabled={sending} className="w-full h-12 gap-2 font-sans font-bold rounded-xl bg-gradient-tropical hover:opacity-90 text-primary-foreground">
                  <Send className="w-4 h-4" />
                  {sending ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ContactPage;
